import React, { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  Typography,
  Collapse,
  CircularProgress,
  Stack,
  Chip,
  IconButton,
  Card,
  CardContent,
  Divider,
  Grid,
} from '@mui/material';
import {
  Close,
  ThumbUp,
  ThumbDown,
  TrendingDown,
} from '@mui/icons-material';
import { apiService } from '../services/api';

const ProviderMatchNotification: React.FC = () => {
  const [matchOpportunities, setMatchOpportunities] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchMatchOpportunities();
    
    // Refresh every 5 minutes
    const interval = setInterval(fetchMatchOpportunities, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchMatchOpportunities = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/seasonal-renewal/provider/match-opportunities');
      
      if (response.success && response.data) {
        setMatchOpportunities(response.data as any);
      }
    } catch (err: any) {
      console.error('Failed to fetch match opportunities:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMatchDecision = async (renewalId: string, decision: 'matched' | 'declined') => {
    try {
      setActionLoading(renewalId);
      setError('');
      
      const response = await apiService.post('/seasonal-renewal/provider/match-decision', {
        renewalId,
        decision,
      });
      
      if (response.success) {
        // Remove this opportunity from the list
        setMatchOpportunities(prev => prev.filter(opp => opp._id !== renewalId));
        
        if (decision === 'matched') {
          alert('Price matched! Your customer will be notified that you\'ve matched the competitor\'s price.');
        } else {
          alert('Declined. The customer will be awarded to the alternative provider.');
        }
      } else {
        setError(response.error || 'Failed to process decision');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to process decision');
    } finally {
      setActionLoading(null);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  if (loading) {
    return null;
  }

  if (matchOpportunities.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mb: 3 }}>
      {matchOpportunities.map((opportunity) => {
        const customer = opportunity.customerId;
        const lowestBid = opportunity.lowestAlternativeBid;
        const currentOffer = opportunity.currentProviderOffer;
        const savings = currentOffer.price - lowestBid.price;
        const competitorProvider = lowestBid.providerId;
        
        return (
          <Card key={opportunity._id} sx={{ mb: 2, border: 2, borderColor: 'warning.main' }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    <TrendingDown sx={{ mr: 1, color: 'warning.main', verticalAlign: 'middle' }} />
                    Renewal Price Match Opportunity
                  </Typography>
                  <Chip label="Action Required" color="warning" size="small" />
                </Box>
              </Stack>

              <Divider sx={{ my: 2 }} />

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Customer
                  </Typography>
                  <Typography variant="body1">
                    {customer.firstName} {customer.lastName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {customer.address?.street}, {customer.address?.city}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Service
                  </Typography>
                  <Typography variant="body1">
                    {opportunity.lastYearServiceType}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {opportunity.lastYearFrequency}
                  </Typography>
                </Grid>
              </Grid>

              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Price Comparison:
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Your Price
                    </Typography>
                    <Typography variant="h6">
                      {formatCurrency(currentOffer.price)}
                    </Typography>
                  </Box>
                  <Typography variant="h6" color="text.secondary">
                    →
                  </Typography>
                  <Box>
                    <Typography variant="caption" color="error.main">
                      Lowest Competitor Bid
                    </Typography>
                    <Typography variant="h6" color="error.main">
                      {formatCurrency(lowestBid.price)}
                    </Typography>
                  </Box>
                  <Box sx={{ ml: 'auto !important' }}>
                    <Typography variant="caption" color="text.secondary">
                      Difference
                    </Typography>
                    <Typography variant="h6" color="warning.main">
                      -{formatCurrency(savings)}
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <Typography variant="body2" color="text.secondary" paragraph>
                <strong>{competitorProvider.businessName || `${competitorProvider.firstName} ${competitorProvider.lastName}`}</strong> has
                submitted a bid of <strong>{formatCurrency(lowestBid.price)}</strong> for this customer's renewal.
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button
                  variant="contained"
                  color="success"
                  size="large"
                  onClick={() => handleMatchDecision(opportunity._id, 'matched')}
                  disabled={actionLoading === opportunity._id}
                  startIcon={actionLoading === opportunity._id ? <CircularProgress size={20} /> : <ThumbUp />}
                  sx={{ flex: 1 }}
                >
                  Match Price - {formatCurrency(lowestBid.price)}
                </Button>
                
                <Button
                  variant="outlined"
                  color="error"
                  size="large"
                  onClick={() => handleMatchDecision(opportunity._id, 'declined')}
                  disabled={actionLoading === opportunity._id}
                  startIcon={<ThumbDown />}
                  sx={{ flex: 1 }}
                >
                  Decline - Let It Go
                </Button>
              </Stack>

              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', textAlign: 'center' }}>
                You have 24 hours to decide. No pressure - just letting you know you have options.
              </Typography>
            </CardContent>
          </Card>
        );
      })}
    </Box>
  );
};

export default ProviderMatchNotification;

